import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';
import { useAuth } from '../../context/AuthContext';
import { extractStrategyRows, getActiveStrategies, normalizeStrategies } from '../../api/strategyLibraryService';
import {
  createWebhookAlert,
  deleteWebhookAlert,
  deleteWebhookEvent,
  getWebhookAlert,
  getWebhookAlerts,
  getWebhookEvent,
  getWebhookEvents,
  getWebhookStats,
  purgeWebhookEventsBefore,
  reprocessWebhookEvent,
  rotateWebhookToken,
  updateWebhookAlert,
} from './chartinkWebhookService';
import './ChartinkWebhook.css';

const EVENT_STATUSES = ['pending', 'processed', 'skipped', 'failed'];
const ACTION_TYPES = ['storeOnly', 'forwardToStrategy', 'addToWatchlist'];
const PAGE_SIZES = [25, 50, 100, 200];
const EMPTY_FORM = {
  name: '',
  description: '',
  isActive: true,
  actionType: 'storeOnly',
  strategySlug: '',
  watchlistId: '',
  tags: [],
};
const EMPTY_EVENT_FILTERS = {
  alertId: '',
  status: '',
  symbol: '',
  from: '',
  to: '',
  page: 1,
  limit: 25,
};

const numberFmt = new Intl.NumberFormat('en-IN');

const getApiOrigin = () => {
  const explicitOrigin = process.env.REACT_APP_API_ORIGIN;
  if (explicitOrigin) return explicitOrigin.replace(/\/$/, '');
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) return apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
  return '';
};

const buildIngestUrl = (token) => {
  const origin = getApiOrigin();
  return origin && token ? `${origin}/api/chartink-webhook/ingest/${token}` : '';
};

const generateIngestToken = () => {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const maskUrl = (url) => {
  if (!url) return 'API origin not configured';
  const token = url.split('/').pop() || '';
  if (token.length <= 12) return url;
  return `${url.slice(0, Math.max(0, url.length - token.length))}${token.slice(0, 4)}...${token.slice(-4)}`;
};

const formatRelative = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const extractMessage = (err, fallback = 'Something went wrong') =>
  err.response?.data?.message || err.response?.data?.error || err.message || fallback;

const normalizeAlerts = (data) => (Array.isArray(data?.alerts) ? data.alerts : []);

const normalizeEventsPayload = (data) => ({
  events: Array.isArray(data?.events) ? data.events : [],
  total: Number(data?.total || 0),
  page: Number(data?.page || 1),
  limit: Number(data?.limit || 25),
  pages: Number(data?.pages || 1),
});

const parseChips = (value) => value.split(',').map((item) => item.trim()).filter(Boolean);

const ChipInput = ({ label, value, onChange, placeholder, helper }) => {
  const [draft, setDraft] = useState('');
  const addItems = useCallback((text) => {
    const items = parseChips(text);
    if (!items.length) return;
    onChange([...(value || []), ...items].filter((item, index, all) => all.indexOf(item) === index));
    setDraft('');
  }, [onChange, value]);

  return (
    <label className="cwh-field">
      <span>{label}</span>
      <div className="cwh-chip-input">
        {(value || []).map((item) => (
          <button type="button" className="cwh-chip" key={item} onClick={() => onChange(value.filter((entry) => entry !== item))} title="Remove">
            {item} <span aria-hidden="true">x</span>
          </button>
        ))}
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => addItems(draft)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              addItems(draft);
            }
          }}
        />
      </div>
      {helper && <small>{helper}</small>}
    </label>
  );
};

const Toast = ({ toast }) => toast ? <div className={`cwh-toast ${toast.type}`}>{toast.text}</div> : null;

const Modal = ({ title, children, onClose, wide = false }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cwh-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className={`cwh-modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <div className="cwh-modal-head">
          <h2>{title}</h2>
          <button type="button" className="cwh-icon-btn" onClick={onClose} aria-label="Close">x</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Drawer = ({ title, children, onClose }) => (
  <div className="cwh-drawer-backdrop" role="presentation" onMouseDown={onClose}>
    <aside className="cwh-drawer" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
      <div className="cwh-modal-head">
        <h2>{title}</h2>
        <button type="button" className="cwh-icon-btn" onClick={onClose} aria-label="Close">x</button>
      </div>
      {children}
    </aside>
  </div>
);

const CopyUrlButton = ({ token, onToast, compact = false }) => {
  const url = buildIngestUrl(token);
  const copy = async () => {
    if (!url) {
      onToast('err', 'Set REACT_APP_API_ORIGIN to enable webhook URL copying.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      onToast('ok', `Copied ${maskUrl(url)}`);
    } catch {
      onToast('err', 'Clipboard copy failed.');
    }
  };
  return <button type="button" className={`cwh-btn ${compact ? 'mini' : 'ghost'}`} onClick={copy}>{compact ? 'Copy' : 'Copy URL'}</button>;
};

const StatusBadge = ({ status, children }) => <span className={`cwh-status-badge ${status || 'pending'}`} aria-label={`Status ${status || 'pending'}`}>{status || 'pending'}{children !== undefined ? `: ${children}` : ''}</span>;

const formatProcessResult = (result) => {
  if (!result) return '—';
  if (typeof result === 'string') return result;
  if (typeof result !== 'object') return String(result);
  const keys = ['inserted', 'replaced', 'added', 'skipped', 'updated', 'failed'];
  const parts = keys.filter((key) => result[key] !== undefined).map((key) => `${key}: ${result[key]}`);
  return parts.length ? parts.join(', ') : JSON.stringify(result).slice(0, 120);
};

const formFromAlert = (alert = {}) => ({
  name: alert.name || '',
  description: alert.description || '',
  isActive: alert.isActive !== false,
  actionType: alert.action?.type || 'storeOnly',
  strategySlug: alert.action?.strategySlug || '',
  watchlistId: alert.action?.watchlistId || '',
  tags: alert.action?.tags || [],
});

const buildAlertBody = (form) => {
  const body = {
    name: form.name.trim(),
    description: form.description.trim(),
    isActive: !!form.isActive,
    action: {
      type: form.actionType,
      tags: form.tags,
    },
  };
  if (form.actionType === 'forwardToStrategy') body.action.strategySlug = form.strategySlug;
  if (form.actionType === 'addToWatchlist') body.action.watchlistId = form.watchlistId;
  return body;
};

const changedOnly = (form, original) => {
  if (!original) return buildAlertBody(form);
  const next = buildAlertBody(form);
  const prev = buildAlertBody(formFromAlert(original));
  return Object.entries(next).reduce((body, [key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify(prev[key])) body[key] = value;
    return body;
  }, {});
};

function AlertFormModal({ alert, strategies, watchlists, onClose, onSave, onToast }) {
  const [form, setForm] = useState(() => alert ? formFromAlert(alert) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (form.actionType === 'forwardToStrategy' && !form.strategySlug) errors.strategySlug = 'Strategy is required';
    if (form.actionType === 'addToWatchlist' && !form.watchlistId) errors.watchlistId = 'Watchlist is required';
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setSaving(true);
    try {
      const body = alert ? changedOnly(form, alert) : { ...buildAlertBody(form), ingestToken: generateIngestToken() };
      const { data } = alert ? await updateWebhookAlert(alert._id, body) : await createWebhookAlert(body);
      const savedAlert = data.alert || data.data || { ...alert, ...body };
      onSave(savedAlert);
    } catch (err) {
      onToast('err', extractMessage(err, 'Alert save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={alert ? 'Edit Chartink Alert' : 'New Chartink Alert'} onClose={onClose} wide>
      <form className="cwh-form" onSubmit={submit}>
        <section className="cwh-form-section open">
          <h3>Basics</h3>
          <div className="cwh-grid-2">
            <label className="cwh-field">
              <span>Name *</span>
              <input value={form.name} maxLength={120} onChange={(event) => setField('name', event.target.value)} className={fieldErrors.name ? 'invalid' : ''} />
              {fieldErrors.name && <small className="cwh-error-text">{fieldErrors.name}</small>}
            </label>
            <label className="cwh-switch-field">
              <span>Active</span>
              <input type="checkbox" checked={form.isActive} onChange={(event) => setField('isActive', event.target.checked)} />
            </label>
          </div>
          <label className="cwh-field">
            <span>Description</span>
            <textarea value={form.description} maxLength={500} rows={3} onChange={(event) => setField('description', event.target.value)} />
          </label>
        </section>

        <details className="cwh-form-section" open>
          <summary>Action</summary>
          <div className="cwh-radio-row">
            {ACTION_TYPES.map((type) => (
              <label key={type} className="cwh-radio-card">
                <input type="radio" name="actionType" checked={form.actionType === type} onChange={() => setField('actionType', type)} />
                <span>{type}</span>
              </label>
            ))}
          </div>
          {form.actionType === 'forwardToStrategy' && (
            <label className="cwh-field">
              <span>Strategy *</span>
              <select value={form.strategySlug} onChange={(event) => setField('strategySlug', event.target.value)} className={fieldErrors.strategySlug ? 'invalid' : ''}>
                <option value="">Select active strategy</option>
                {strategies.map((strategy) => <option key={strategy.slug} value={strategy.slug}>{strategy.name} ({strategy.slug})</option>)}
              </select>
              {fieldErrors.strategySlug && <small className="cwh-error-text">{fieldErrors.strategySlug}</small>}
            </label>
          )}
          {form.actionType === 'addToWatchlist' && (
            <label className="cwh-field">
              <span>Watchlist *</span>
              <select value={form.watchlistId} onChange={(event) => setField('watchlistId', event.target.value)} className={fieldErrors.watchlistId ? 'invalid' : ''}>
                <option value="">Select watchlist</option>
                {watchlists.map((watchlist) => <option key={watchlist.watchlistId || watchlist._id} value={watchlist.watchlistId || watchlist._id}>{watchlist.watchlistName || watchlist.name}</option>)}
              </select>
              {fieldErrors.watchlistId && <small className="cwh-error-text">{fieldErrors.watchlistId}</small>}
            </label>
          )}
          <ChipInput label="Tags" value={form.tags} onChange={(value) => setField('tags', value)} placeholder="Comma or Enter" />
        </details>

        <div className="cwh-form-actions">
          <button type="button" className="cwh-btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cwh-btn primary" disabled={saving}>{saving ? 'Saving...' : 'Save Alert'}</button>
        </div>
      </form>
    </Modal>
  );
}

function UrlReadyModal({ alert, onClose, onToast }) {
  const url = buildIngestUrl(alert?.ingestToken);
  return (
    <Modal title="Webhook URL Ready" onClose={onClose}>
      <div className="cwh-url-ready">
        <div className="cwh-alert-note">Paste this URL in Chartink under Create/Edit Alert, then Webhook URL.</div>
        <code>{url || 'Set REACT_APP_API_ORIGIN to build the public ingest URL.'}</code>
        <CopyUrlButton token={alert?.ingestToken} onToast={onToast} />
      </div>
    </Modal>
  );
}

export default function ChartinkWebhook() {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [eventsPayload, setEventsPayload] = useState({ events: [], total: 0, page: 1, limit: 25, pages: 1 });
  const [strategies, setStrategies] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [toast, setToast] = useState(null);
  const [alertSearch, setAlertSearch] = useState('');
  const [showInfo, setShowInfo] = useState(() => localStorage.getItem('chartinkWebhookInfoDismissed') !== '1');
  const [editingAlert, setEditingAlert] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [urlReadyAlert, setUrlReadyAlert] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedStocks, setExpandedStocks] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [deleteAlertTarget, setDeleteAlertTarget] = useState(null);

  const activeTab = searchParams.get('tab') === 'events' ? 'events' : 'alerts';
  const eventFilters = useMemo(() => ({
    ...EMPTY_EVENT_FILTERS,
    alertId: searchParams.get('alertId') || '',
    status: searchParams.get('status') || '',
    symbol: searchParams.get('symbol') || '',
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 25),
  }), [searchParams]);

  const showToast = useCallback((type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const updateParams = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await getWebhookStats();
      setStats(data.stats || data.data || data);
    } catch (err) {
      showToast('err', extractMessage(err, 'Failed to load webhook stats'));
    } finally {
      setLoadingStats(false);
    }
  }, [showToast]);

  const loadAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const { data } = await getWebhookAlerts();
      setAlerts(normalizeAlerts(data));
    } catch (err) {
      showToast('err', extractMessage(err, 'Failed to load alerts'));
      setAlerts([]);
    } finally {
      setLoadingAlerts(false);
    }
  }, [showToast]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const params = Object.entries(eventFilters).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {});
      if (params.from) params.from = new Date(`${params.from}T00:00:00`).toISOString();
      if (params.to) params.to = new Date(`${params.to}T23:59:59`).toISOString();
      const { data } = await getWebhookEvents(params);
      setEventsPayload(normalizeEventsPayload(data));
    } catch (err) {
      showToast('err', extractMessage(err, 'Failed to load events'));
      setEventsPayload({ events: [], total: 0, page: 1, limit: eventFilters.limit, pages: 1 });
    } finally {
      setLoadingEvents(false);
    }
  }, [eventFilters, showToast]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [{ data: strategyData }, watchlistResult] = await Promise.all([
        getActiveStrategies(),
        user?.id || user?._id ? api.get(`/watchlist/user/${user.id || user._id}`).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
      ]);
      setStrategies(normalizeStrategies(extractStrategyRows(strategyData)));
      setWatchlists(watchlistResult.data?.data || watchlistResult.data?.watchlists || []);
    } catch {
      setStrategies([]);
      setWatchlists([]);
    }
  }, [user?.id, user?._id]);

  useEffect(() => {
    loadStats();
    loadAlerts();
    loadReferenceData();
  }, [loadAlerts, loadReferenceData, loadStats]);

  useEffect(() => {
    if (activeTab === 'events') loadEvents();
  }, [activeTab, loadEvents]);

  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      loadStats();
    };
    const timer = setInterval(tick, 30000);
    return () => clearInterval(timer);
  }, [loadStats]);

  useEffect(() => {
    if (!autoRefresh || activeTab !== 'events') return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) loadEvents();
    }, 15000);
    return () => clearInterval(timer);
  }, [activeTab, autoRefresh, loadEvents]);

  const filteredAlerts = useMemo(() => {
    const q = alertSearch.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter((alert) => `${alert.name || ''} ${alert.description || ''}`.toLowerCase().includes(q));
  }, [alertSearch, alerts]);

  const strategyBySlug = useMemo(() => Object.fromEntries(strategies.map((strategy) => [strategy.slug, strategy])), [strategies]);
  const watchlistById = useMemo(() => Object.fromEntries(watchlists.map((watchlist) => [String(watchlist.watchlistId || watchlist._id), watchlist])), [watchlists]);

  const openEditAlert = async (alert) => {
    try {
      const { data } = await getWebhookAlert(alert._id);
      setEditingAlert(data.alert || data.data || alert);
      setShowAlertModal(true);
    } catch (err) {
      showToast('err', extractMessage(err, 'Failed to load alert'));
    }
  };

  const onSavedAlert = async (alert) => {
    setShowAlertModal(false);
    setEditingAlert(null);
    setUrlReadyAlert(alert);
    showToast('ok', 'Alert saved.');
    await Promise.all([loadAlerts(), loadStats()]);
  };

  const toggleAlert = async (alert, isActive) => {
    try {
      await updateWebhookAlert(alert._id, { isActive });
      setAlerts((rows) => rows.map((row) => row._id === alert._id ? { ...row, isActive } : row));
      showToast('ok', isActive ? 'Alert activated.' : 'Alert paused.');
      loadStats();
    } catch (err) {
      showToast('err', extractMessage(err, 'Toggle failed'));
    }
  };

  const rotateToken = async (alert) => {
    if (!window.confirm('This invalidates the current URL and any Chartink alert still using it will stop working until you update it. Continue?')) return;
    try {
      const { data } = await rotateWebhookToken(alert._id);
      const updated = data.alert || data.data || { ...alert, ingestToken: data.ingestToken };
      setUrlReadyAlert(updated);
      await loadAlerts();
    } catch (err) {
      showToast('err', extractMessage(err, 'Token rotation failed'));
    }
  };

  const removeAlert = async (alert, deleteEvents = false) => {
    try {
      const { data } = await deleteWebhookAlert(alert._id, deleteEvents);
      const count = data.deletedEvents || data.deletedEventCount;
      showToast('ok', count ? `Alert deleted. ${count} events removed.` : 'Alert deleted.');
      setDeleteAlertTarget(null);
      await Promise.all([loadAlerts(), loadStats(), loadEvents()]);
    } catch (err) {
      showToast('err', extractMessage(err, 'Delete failed'));
    }
  };

  const openEventDetail = async (eventRow) => {
    setLoadingDetail(true);
    try {
      const { data } = await getWebhookEvent(eventRow._id);
      setDetailEvent(data.event || data.data || eventRow);
    } catch (err) {
      showToast('err', extractMessage(err, 'Failed to load event'));
    } finally {
      setLoadingDetail(false);
    }
  };

  const reprocessEvent = async (eventRow) => {
    try {
      const { data } = await reprocessWebhookEvent(eventRow._id);
      const updated = data.event || data.data || data;
      showToast('ok', `Reprocessed: ${updated.processStatus || 'updated'} ${formatProcessResult(updated.processResult)}`);
      await Promise.all([loadEvents(), loadStats()]);
      if (detailEvent?._id === eventRow._id) setDetailEvent(updated);
    } catch (err) {
      showToast('err', extractMessage(err, 'Reprocess failed'));
    }
  };

  const removeEvent = async (eventRow) => {
    if (!window.confirm('Delete this webhook event permanently?')) return;
    try {
      await deleteWebhookEvent(eventRow._id);
      showToast('ok', 'Event deleted.');
      if (detailEvent?._id === eventRow._id) setDetailEvent(null);
      await Promise.all([loadEvents(), loadStats()]);
    } catch (err) {
      showToast('err', extractMessage(err, 'Event delete failed'));
    }
  };

  const purgeEvents = async ({ before, password }) => {
    const { data } = await purgeWebhookEventsBefore({ before, password });
    const deleted = data?.deletedCount ?? data?.deleted ?? 0;
    showToast('ok', `${numberFmt.format(deleted)} events deleted (on/before ${before}).`);
    setPurgeOpen(false);
    await Promise.all([loadEvents(), loadStats()]);
    return data;
  };

  const start = eventsPayload.total === 0 ? 0 : ((eventsPayload.page - 1) * eventsPayload.limit) + 1;
  const end = Math.min(eventsPayload.total, eventsPayload.page * eventsPayload.limit);

  return (
    <AppLayout pageBadge="Chartink Webhook">
      <div className="cwh-root">
        <Toast toast={toast} />

        <header className="cwh-header">
          <div>
            <h1>Chartink Webhook</h1>
            <p>Design alert automations, copy ingest URLs, and audit every incoming Chartink payload.</p>
          </div>
          <button type="button" className="cwh-btn ghost" onClick={() => { loadStats(); loadAlerts(); if (activeTab === 'events') loadEvents(); }}>Refresh</button>
        </header>

        <section className="cwh-stats-grid" aria-label="Webhook statistics">
          {[
            ['Total Alerts', stats?.totalAlerts],
            ['Active Alerts', stats?.activeAlerts],
            ['Events 24h', stats?.eventsLast24h],
          ].map(([label, value]) => (
            <div className="cwh-stat-card" key={label}>
              <span>{label}</span>
              <strong>{loadingStats ? '...' : numberFmt.format(value || 0)}</strong>
            </div>
          ))}
          <div className="cwh-stat-card status-card">
            <span>By Status</span>
            <div className="cwh-status-row">
              {EVENT_STATUSES.map((status) => <StatusBadge key={status} status={`${status}`}>{numberFmt.format(stats?.byStatus?.[status] || 0)}</StatusBadge>)}
            </div>
            <div className="cwh-status-counts">
              {EVENT_STATUSES.map((status) => <span key={status}>{status}: {numberFmt.format(stats?.byStatus?.[status] || 0)}</span>)}
            </div>
          </div>
        </section>

        <div className="cwh-tabs" role="tablist">
          <button type="button" className={activeTab === 'alerts' ? 'active' : ''} onClick={() => updateParams({ tab: 'alerts', page: null })}>Alerts</button>
          <button type="button" className={activeTab === 'events' ? 'active' : ''} onClick={() => updateParams({ tab: 'events' })}>Events</button>
        </div>

        {activeTab === 'alerts' ? (
          <section className="cwh-panel">
            {showInfo && (
              <div className="cwh-info-banner">
                <span>Paste the ingest URL into Chartink Create/Edit Alert, then Webhook URL. Processing errors appear in Events as failed and can be reprocessed.</span>
                <button type="button" onClick={() => { localStorage.setItem('chartinkWebhookInfoDismissed', '1'); setShowInfo(false); }}>Dismiss</button>
              </div>
            )}

            <div className="cwh-toolbar">
              <input value={alertSearch} onChange={(event) => setAlertSearch(event.target.value)} placeholder="Search alerts" />
              {isAdmin && <button type="button" className="cwh-btn primary" onClick={() => { setEditingAlert(null); setShowAlertModal(true); }}>New Alert</button>}
            </div>

            {loadingAlerts ? <div className="cwh-empty">Loading alerts...</div> : filteredAlerts.length === 0 ? (
              <div className="cwh-empty"><div className="cwh-empty-icon">🔗</div>{isAdmin ? 'Create your first alert.' : 'No alerts configured yet.'}</div>
            ) : (
              <>
                <div className="cwh-table-wrap">
                  <table className="cwh-table">
                    <thead>
                      <tr><th>Name</th><th>Active</th><th>Action</th><th>Triggers</th><th>Status</th><th>URL</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {filteredAlerts.map((alert) => {
                        const target = alert.action?.type === 'forwardToStrategy' ? strategyBySlug[alert.action?.strategySlug]?.name || alert.action?.strategySlug : watchlistById[String(alert.action?.watchlistId)]?.watchlistName || alert.action?.watchlistId;
                        return (
                          <tr key={alert._id}>
                            <td><strong>{alert.name}</strong><small>{alert.description}</small></td>
                            <td>{isAdmin ? <input type="checkbox" checked={alert.isActive !== false} onChange={(event) => toggleAlert(alert, event.target.checked)} /> : alert.isActive !== false ? 'Yes' : 'No'}</td>
                            <td><span className="cwh-soft-badge">{alert.action?.type || 'storeOnly'}</span><small>{target || '—'}</small></td>
                            <td>{numberFmt.format(alert.triggerCount || 0)}<small title={formatDateTime(alert.lastTriggeredAt)}>{formatRelative(alert.lastTriggeredAt)}</small></td>
                            <td><span className={`cwh-dot ${alert.lastError ? 'bad' : alert.isActive === false ? 'idle' : 'ok'}`} title={alert.lastError || ''} />{alert.lastError ? 'Error' : alert.isActive === false ? 'Paused' : 'Ready'}</td>
                            <td><span className="cwh-url-preview">{maskUrl(buildIngestUrl(alert.ingestToken))}</span><CopyUrlButton token={alert.ingestToken} onToast={showToast} compact /></td>
                            <td><div className="cwh-actions">{isAdmin && <><button className="cwh-btn mini" onClick={() => rotateToken(alert)}>Rotate</button><button className="cwh-btn mini" onClick={() => openEditAlert(alert)}>Edit</button><button className="cwh-btn mini danger" onClick={() => setDeleteAlertTarget(alert)}>Delete</button></>}</div></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="cwh-card-list">
                  {filteredAlerts.map((alert) => (
                    <article className="cwh-mobile-card" key={alert._id}>
                      <div><strong>{alert.name}</strong><StatusBadge status={alert.lastError ? 'failed' : alert.isActive === false ? 'skipped' : 'processed'} /></div>
                      <p>{alert.description || 'No description'}</p>
                      <span>{alert.action?.type || 'storeOnly'} · {numberFmt.format(alert.triggerCount || 0)} triggers</span>
                      <CopyUrlButton token={alert.ingestToken} onToast={showToast} />
                      {isAdmin && <div className="cwh-actions"><button className="cwh-btn mini" onClick={() => openEditAlert(alert)}>Edit</button><button className="cwh-btn mini danger" onClick={() => setDeleteAlertTarget(alert)}>Delete</button></div>}
                    </article>
                  ))}
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="cwh-panel">
            <div className="cwh-filter-grid">
              <select value={eventFilters.alertId} onChange={(event) => updateParams({ alertId: event.target.value, page: 1 })}><option value="">All alerts</option>{alerts.map((alert) => <option key={alert._id} value={alert._id}>{alert.name}</option>)}</select>
              <select value={eventFilters.status} onChange={(event) => updateParams({ status: event.target.value, page: 1 })}><option value="">All statuses</option>{EVENT_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}</select>
              <input value={eventFilters.symbol} placeholder="Symbol" onChange={(event) => updateParams({ symbol: event.target.value, page: 1 })} onBlur={(event) => updateParams({ symbol: event.target.value.toUpperCase(), page: 1 })} />
              <input type="date" value={eventFilters.from} onChange={(event) => updateParams({ from: event.target.value, page: 1 })} />
              <input type="date" value={eventFilters.to} onChange={(event) => updateParams({ to: event.target.value, page: 1 })} />
              <select value={eventFilters.limit} onChange={(event) => updateParams({ limit: event.target.value, page: 1 })}>{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}</select>
            </div>
            <div className="cwh-toolbar">
              <div className="cwh-actions"><button type="button" className="cwh-btn ghost" onClick={loadEvents}>Refresh</button><label className="cwh-inline-check"><input type="checkbox" checked={autoRefresh} onChange={(event) => setAutoRefresh(event.target.checked)} /> Auto-refresh</label></div>
              {isAdmin && <button type="button" className="cwh-btn danger" onClick={() => setPurgeOpen(true)}>Purge Older Events</button>}
            </div>

            {loadingEvents ? <div className="cwh-empty">Loading events...</div> : eventsPayload.events.length === 0 ? <div className="cwh-empty">No events yet — waiting for Chartink to trigger.</div> : (
              <>
                <div className="cwh-table-wrap">
                  <table className="cwh-table">
                    <thead><tr><th>Received</th><th>Alert</th><th>Scan</th><th>Stocks</th><th>Status</th><th>Result / Error</th><th>Actions</th></tr></thead>
                    <tbody>
                      {eventsPayload.events.map((eventRow) => (
                        <tr key={eventRow._id}>
                          <td title={formatDateTime(eventRow.receivedAt)}>{formatRelative(eventRow.receivedAt)}</td>
                          <td><button className="cwh-link-btn" onClick={() => updateParams({ tab: 'events', alertId: eventRow.alertId, page: 1 })}>{eventRow.alertName || '—'}</button></td>
                          <td><strong>{eventRow.scanName || '—'}</strong><small>{eventRow.scanUrl}</small></td>
                          <td><button className="cwh-link-btn" onClick={() => setExpandedStocks((prev) => ({ ...prev, [eventRow._id]: !prev[eventRow._id] }))}>{numberFmt.format(eventRow.stocksCount || eventRow.stocks?.length || 0)}</button>{expandedStocks[eventRow._id] && <div className="cwh-stock-chips">{(eventRow.stocks || []).slice(0, 40).map((stock) => <span key={stock.symbol}>{stock.symbol}</span>)}</div>}</td>
                          <td><StatusBadge status={eventRow.processStatus} /></td>
                          <td>{eventRow.processStatus === 'processed' ? formatProcessResult(eventRow.processResult) : eventRow.processError || '—'}</td>
                          <td><div className="cwh-actions"><button className="cwh-btn mini" onClick={() => openEventDetail(eventRow)}>View</button>{isAdmin && eventRow.processStatus !== 'processed' && <button className="cwh-btn mini" onClick={() => reprocessEvent(eventRow)}>Reprocess</button>}{isAdmin && <button className="cwh-btn mini danger" onClick={() => removeEvent(eventRow)}>Delete</button>}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cwh-card-list">
                  {eventsPayload.events.map((eventRow) => <article className="cwh-mobile-card" key={eventRow._id}><div><strong>{eventRow.alertName}</strong><StatusBadge status={eventRow.processStatus} /></div><p>{eventRow.scanName}</p><span>{formatRelative(eventRow.receivedAt)} · {numberFmt.format(eventRow.stocksCount || 0)} stocks</span><div className="cwh-actions"><button className="cwh-btn mini" onClick={() => openEventDetail(eventRow)}>View</button>{isAdmin && <button className="cwh-btn mini danger" onClick={() => removeEvent(eventRow)}>Delete</button>}</div></article>)}
                </div>
                <div className="cwh-pagination"><span>Showing {start}-{end} of {numberFmt.format(eventsPayload.total)}</span><button className="cwh-btn mini" disabled={eventsPayload.page <= 1} onClick={() => updateParams({ page: eventsPayload.page - 1 })}>Prev</button><input type="number" min="1" max={eventsPayload.pages} value={eventsPayload.page} onChange={(event) => updateParams({ page: event.target.value || 1 })} /><span>of {eventsPayload.pages}</span><button className="cwh-btn mini" disabled={eventsPayload.page >= eventsPayload.pages} onClick={() => updateParams({ page: eventsPayload.page + 1 })}>Next</button></div>
              </>
            )}
          </section>
        )}

        {showAlertModal && <AlertFormModal alert={editingAlert} strategies={strategies} watchlists={watchlists} onClose={() => { setShowAlertModal(false); setEditingAlert(null); }} onSave={onSavedAlert} onToast={showToast} />}
        {urlReadyAlert && <UrlReadyModal alert={urlReadyAlert} onClose={() => setUrlReadyAlert(null)} onToast={showToast} />}
        {purgeOpen && <PurgeEventsModal totalEvents={stats?.totalEvents} onClose={() => setPurgeOpen(false)} onSubmit={purgeEvents} />}
        {deleteAlertTarget && <DeleteAlertModal alert={deleteAlertTarget} onClose={() => setDeleteAlertTarget(null)} onDelete={removeAlert} />}
        {loadingDetail && <div className="cwh-detail-loading">Loading event...</div>}
        {detailEvent && <EventDetailDrawer eventRow={detailEvent} isAdmin={isAdmin} onClose={() => setDetailEvent(null)} onReprocess={reprocessEvent} onDelete={removeEvent} />}
      </div>
    </AppLayout>
  );
}

function PurgeEventsModal({ totalEvents, onClose, onSubmit }) {
  const [step, setStep] = useState(1);
  const [before, setBefore] = useState('');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    if (!before) { setError('Pick a cutoff date.'); return; }
    if (!password.trim()) { setError('Enter your admin password.'); return; }
    setError('');
    setStep(2);
  };

  const handleConfirm = async () => {
    if (confirmText.trim() !== 'DELETE') {
      setError('Type DELETE exactly to confirm.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit({ before, password });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Purge failed.';
      setError(msg);
      setStep(1);
      setPassword('');
      setConfirmText('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Purge Events — Step ${step}/2`} onClose={loading ? () => {} : onClose}>
      <div className="cwh-form">
        <div className="cwh-alert-note">
          Permanently deletes <strong>all</strong> webhook events received on or before the selected date, across every alert.
          {typeof totalEvents === 'number' && <> Current total events in DB: <strong>{totalEvents.toLocaleString()}</strong>.</>}
        </div>

        {step === 1 && (
          <>
            <label className="cwh-field">
              <span>Delete events on or before</span>
              <input type="date" value={before} onChange={(e) => setBefore(e.target.value)} autoFocus />
            </label>
            <label className="cwh-field">
              <span>Admin Password</span>
              <input
                type="password"
                placeholder="Your login password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNext()}
              />
            </label>
          </>
        )}

        {step === 2 && (
          <>
            <div className="cwh-alert-note" style={{ background: 'rgba(220, 38, 38, 0.15)', border: '1px solid rgba(220, 38, 38, 0.4)' }}>
              This will permanently delete every webhook event with <code>receivedAt</code> on or before <strong>{before}</strong>. This cannot be undone.
            </div>
            <label className="cwh-field">
              <span>Type <code>DELETE</code> to confirm</span>
              <input
                type="text"
                placeholder="DELETE"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                autoFocus
              />
            </label>
          </>
        )}

        {error && <div className="cwh-alert-note" style={{ color: '#fca5a5' }}>{error}</div>}

        <div className="cwh-form-actions">
          <button className="cwh-btn ghost" onClick={onClose} disabled={loading}>Cancel</button>
          {step === 1 && <button className="cwh-btn danger" onClick={handleNext}>Next</button>}
          {step === 2 && (
            <>
              <button className="cwh-btn ghost" onClick={() => { setStep(1); setConfirmText(''); setError(''); }} disabled={loading}>Back</button>
              <button className="cwh-btn danger" onClick={handleConfirm} disabled={loading || confirmText.trim() !== 'DELETE'}>
                {loading ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function DeleteAlertModal({ alert, onClose, onDelete }) {
  const [deleteEvents, setDeleteEvents] = useState(false);
  return (
    <Modal title="Delete Alert" onClose={onClose}>
      <div className="cwh-form">
        <div className="cwh-alert-note">Delete "{alert.name}"? This permanently removes the alert and invalidates its ingest URL.</div>
        <label className="cwh-inline-check"><input type="checkbox" checked={deleteEvents} onChange={(event) => setDeleteEvents(event.target.checked)} /> Also delete all stored events for this alert</label>
        <div className="cwh-form-actions"><button className="cwh-btn ghost" onClick={onClose}>Cancel</button><button className="cwh-btn danger" onClick={() => onDelete(alert, deleteEvents)}>Delete Alert</button></div>
      </div>
    </Modal>
  );
}

function EventDetailDrawer({ eventRow, isAdmin, onClose, onReprocess, onDelete }) {
  return (
    <Drawer title="Webhook Event" onClose={onClose}>
      <div className="cwh-detail">
        <section><h3>Meta</h3><dl><dt>Alert</dt><dd>{eventRow.alertName}</dd><dt>Scan</dt><dd>{eventRow.scanName}</dd><dt>Scan URL</dt><dd>{eventRow.scanUrl}</dd><dt>Triggered Raw</dt><dd>{eventRow.triggeredAtRaw || '—'}</dd><dt>Received</dt><dd>{formatDateTime(eventRow.receivedAt)}</dd><dt>Source IP</dt><dd>{eventRow.sourceIp || '—'}</dd><dt>User Agent</dt><dd>{eventRow.userAgent || '—'}</dd><dt>Status</dt><dd><StatusBadge status={eventRow.processStatus} /></dd><dt>Processed</dt><dd>{formatDateTime(eventRow.processedAt)}</dd></dl></section>
        <section><h3>Stocks</h3><div className="cwh-stock-table">{(eventRow.stocks || []).map((stock) => <div key={stock.symbol}><span>{stock.symbol}</span><span>{stock.triggerPrice ?? '—'}</span></div>)}</div></section>
        {eventRow.processError && <section><h3>Process Error</h3><pre className="cwh-error-block">{eventRow.processError}</pre></section>}
        <details open><summary>Process Result</summary><pre>{JSON.stringify(eventRow.processResult, null, 2)}</pre></details>
        <details><summary>Raw Payload</summary><pre>{JSON.stringify(eventRow.rawPayload, null, 2)}</pre></details>
        <div className="cwh-form-actions"><button className="cwh-btn ghost" onClick={onClose}>Close</button>{isAdmin && <button className="cwh-btn primary" onClick={() => onReprocess(eventRow)}>Reprocess</button>}{isAdmin && <button className="cwh-btn danger" onClick={() => onDelete(eventRow)}>Delete</button>}</div>
      </div>
    </Drawer>
  );
}
